import { fetchSpeechAudio, fetchVoiceSettings } from "src/queries/hooks/speech";
import { getFlag } from "src/utils/helpers/ldClient";
import { VOICE_PREFERENCES_FEATURE_FLAGS } from "src/pages/Settings/Channel/Voice/PhoneChannelConsts";
import { SYSTEM_MODES } from "./createAppSlice";
import { buildVoiceNamesMap, downloadFile } from "@utils";

const createSpeechPlayerSlice = (set, get) => {
  let audioInstance = null;
  let currentAudioUrl = null;
  let currentAudioBlob = null;
  let abortController = null;

  const cleanupAudio = () => {
    if (audioInstance) {
      audioInstance.pause();
      audioInstance.removeEventListener("ended", audioInstance._endedHandler);
      audioInstance.removeEventListener("error", audioInstance._errorHandler);
      audioInstance.removeEventListener("timeupdate", audioInstance._timeUpdateHandler);
      audioInstance.removeEventListener("loadedmetadata", audioInstance._loadedMetadataHandler);
      audioInstance = null;
    }
    if (currentAudioUrl) {
      URL.revokeObjectURL(currentAudioUrl);
      currentAudioUrl = null;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return {
    speechPlayer: {
      currentAudioId: null,
      currentInstanceId: null,
      isPlaying: false,
      isPaused: false,
      isLoading: false,
      isOpen: false,
      error: null,
      voiceName: null,
      text: null,
      progress: 0,
      duration: 0
    },

    setSpeechPlayerState: (updates) => {
      set({ speechPlayer: { ...get().speechPlayer, ...updates } });
    },

    openSpeechPlayer: () => {
      const { setSpeechPlayerState } = get();
      setSpeechPlayerState({ isOpen: true });
    },

    closeSpeechPlayer: () => {
      const { setSpeechPlayerState, stopSpeech } = get();
      if (!get().speechPlayer.isOpen) return;
      stopSpeech();
      setSpeechPlayerState({ isOpen: false });
    },

    playSpeech: async ({ text, instanceId, isAdmin = false }) => {
      const { setSpeechPlayerState, stopSpeech, resumeSpeech, speechPlayer, selectedAccountId, selectedAssistant, openSpeechPlayer } = get();

      if (!speechPlayer.isOpen) {
        openSpeechPlayer();
      }

      // If same input is paused, just resume
      if (speechPlayer.isPaused && speechPlayer.currentInstanceId === instanceId && audioInstance) {
        resumeSpeech();
        return;
      }

      // If same input is already playing, do nothing
      if (!speechPlayer.isLoading && speechPlayer.isPlaying && speechPlayer.currentInstanceId === instanceId) {
        return;
      }

      stopSpeech();

      const audioId = `${instanceId}-${text}`;

      abortController = new AbortController();
      const currentAbortController = abortController;

      setSpeechPlayerState({
        currentAudioId: audioId,
        currentInstanceId: instanceId,
        isLoading: true,
        error: null,
        isPaused: false,
        text,
        progress: 0
      });

      try {
        if (!selectedAccountId || !selectedAssistant?.assistantId) {
          throw new Error("Account or assistant not found");
        }
        const { draft, systemMode } = get();

        const voiceSettings = await fetchVoiceSettings({
          accountId: selectedAccountId,
          assistantId: selectedAssistant?.assistantId,
          systemMode,
          signal: currentAbortController.signal
        });

        const isEditMode = systemMode === SYSTEM_MODES.EDIT;
        const draftSettings = draft ? voiceSettings.draftSettings : voiceSettings.settings;
        const selectedVoiceSettings = isEditMode ? draftSettings : voiceSettings.settings;

        // Check if aborted
        if (currentAbortController.signal.aborted) {
          return;
        }

        const { telephony } = selectedVoiceSettings;
        const voiceName = telephony?.voiceName;
        const speakingRate = telephony?.speakingRate ? parseFloat(telephony.speakingRate) : 1.0;
        const pitch = telephony?.pitch ? parseFloat(telephony.pitch) : 0.0;
        const voiceTypes = selectedVoiceSettings?.options?.voiceTypes;

        const useAllVoiceOptions = getFlag(VOICE_PREFERENCES_FEATURE_FLAGS.SHOW_ALL_VOICE_OPTIONS);
        const allVoiceOptions = voiceTypes?.active || {};
        const legacyVoiceOptions = voiceTypes?.legacy || {};
        const voiceOptions = useAllVoiceOptions
          ? allVoiceOptions
          : Object.fromEntries(
            Object.entries(allVoiceOptions).filter(([_, voiceOption]) => voiceOption.exposed)
          );

        if (!voiceOptions?.[voiceName] && legacyVoiceOptions?.[voiceName]) {
          voiceOptions[voiceName] = legacyVoiceOptions?.[voiceName];
        }
        if (!voiceOptions?.[voiceName] && allVoiceOptions?.[voiceName]) {
          voiceOptions[voiceName] = allVoiceOptions?.[voiceName];
        }
        const friendlyVoiceName = voiceOptions?.[voiceName]?.friendlyName;
        if (!friendlyVoiceName) {
          throw new Error("Voice name not found in voice options");
        }

        const configuredLanguages = get().languages?.current?.list ?? [];
        const defaultLanguage = get().languages?.current?.default ?? configuredLanguages[0] ?? "ENGLISH";
        const selectedLanguage = get().currentSelectedLanguage ?? defaultLanguage;
        const voiceNames = buildVoiceNamesMap(configuredLanguages, voiceName, defaultLanguage);
        const language = selectedLanguage.toUpperCase();

        const audioBlob = await fetchSpeechAudio({
          text,
          voiceNames,
          language,
          speakingRate,
          pitch,
          isAdmin,
          systemMode,
          signal: currentAbortController.signal
        });

        // Check if this request was aborted or a new one started
        if (currentAbortController.signal.aborted || get().speechPlayer.currentAudioId !== audioId) {
          return;
        }

        currentAudioBlob = audioBlob;
        currentAudioUrl = URL.createObjectURL(audioBlob);
        audioInstance = new Audio(currentAudioUrl);

        const handleEnded = () => {
          setSpeechPlayerState({ isPlaying: false, isPaused: false });
          cleanupAudio();
        };

        const handleError = (e) => {
          console.error("Audio playback error:", e);
          setSpeechPlayerState({
            isPlaying: false,
            isPaused: false,
            error: "Failed to play audio",
            currentAudioId: null,
            currentInstanceId: null,
            voiceName: null,
            text: null
          });
          cleanupAudio();
        };

        const handleTimeUpdate = () => {
          if (audioInstance) {
            const progress = audioInstance.currentTime / audioInstance.duration;
            setSpeechPlayerState({
              progress: isNaN(progress) ? 0 : progress,
              duration: audioInstance.duration
            });
          }
        };

        const handleLoadedMetadata = () => {
          if (audioInstance) {
            setSpeechPlayerState({ duration: audioInstance.duration });
          }
        };

        audioInstance._endedHandler = handleEnded;
        audioInstance._errorHandler = handleError;
        audioInstance._timeUpdateHandler = handleTimeUpdate;
        audioInstance._loadedMetadataHandler = handleLoadedMetadata;
        audioInstance.addEventListener("ended", handleEnded);
        audioInstance.addEventListener("error", handleError);
        audioInstance.addEventListener("timeupdate", handleTimeUpdate);
        audioInstance.addEventListener("loadedmetadata", handleLoadedMetadata);

        await audioInstance.play();
        setSpeechPlayerState({ isPlaying: true, isLoading: false, isPaused: false, voiceName: friendlyVoiceName });
      } catch (error) {
        // Don't show error if request was aborted
        if (error.name === "AbortError" || currentAbortController.signal.aborted) {
          return;
        }

        console.error("Error generating speech:", error);
        const errorMessage = error.message || "Failed to generate speech";
        setSpeechPlayerState({
          isLoading: false,
          isPlaying: false,
          isPaused: false,
          error: errorMessage,
          currentAudioId: null,
          currentInstanceId: null,
          voiceName: null
        });
        cleanupAudio();
      }
    },

    pauseSpeech: () => {
      const { setSpeechPlayerState } = get();

      if (audioInstance && !audioInstance.paused) {
        audioInstance.pause();
        setSpeechPlayerState({ isPlaying: false, isPaused: true });
      }
    },

    resumeSpeech: () => {
      const { setSpeechPlayerState, speechPlayer } = get();

      if (audioInstance && audioInstance.paused && speechPlayer.currentAudioId) {
        audioInstance.play();
        setSpeechPlayerState({ isPlaying: true, isPaused: false });
      }
    },

    stopSpeech: () => {
      const { setSpeechPlayerState } = get();

      cleanupAudio();
      currentAudioBlob = null;
      setSpeechPlayerState({
        currentAudioId: null,
        currentInstanceId: null,
        isPlaying: false,
        isPaused: false,
        isLoading: false,
        error: null,
        progress: 0,
        duration: 0
      });
    },

    downloadCurrentAudio: () => {
      if (!currentAudioBlob) return;

      const { selectedAssistant, speechPlayer } = get();
      const agentName = selectedAssistant?.name || "agent";
      const utteranceId = speechPlayer.currentInstanceId || "utterance";
      const filename = `${agentName}_${utteranceId}.wav`;

      const url = URL.createObjectURL(currentAudioBlob);
      downloadFile(url, filename);
      URL.revokeObjectURL(url);
    }
  };
};

export default createSpeechPlayerSlice;
